import { createSelector } from '@ngrx/store'
import { sharedSelectors } from 'shared-lib'
import * as customerSelectors from './customer/customer.selectors'
import * as searchKeySelectors from './search-key/search-key.selectors'

// Active customer scope (act-as / client-switch) rides in the sign-in token's claims now, not the IUser doc,
// so override the SDK's user-doc-based selectors to derive the active customerKey from the decoded token instead.
const getCustomerKey = createSelector(sharedSelectors.getCurrentTokenDecoded, token => token != null && token.customerKey)
const getCustomerKey$ = createSelector(sharedSelectors.getCurrentTokenDecoded, token => token?.customerKey)
// Human-readable active-customer name for the client-switch/act-as banners: prefers the backend
// `customerName` claim, falling back to the key only when a session predates the claim.
const getCustomerName$ = createSelector(sharedSelectors.getCurrentTokenDecoded, token => (token as any)?.customerName ?? token?.customerKey ?? '')
const getCustomerNameClaim = createSelector(
  sharedSelectors.getCurrentTokenDecoded,
  token => ((token as any)?.customerName as string | undefined) ?? ''
)

// Impersonation (admin "Act as" handoff, Story 1.3/1.4) claims ride on the sign-in token too, but are not
// declared on IE11Token (real runtime claims minted by the backend, absent from the shared-lib interface).
// Keep the `as any` casts confined to this one file — components/templates consume typed selector output.
const getIsImpersonating = createSelector(sharedSelectors.getCurrentTokenDecoded, token => (token as any)?.impersonating === true)
const getActAsExpiresAt = createSelector(sharedSelectors.getCurrentTokenDecoded, token => (token as any)?.actAsExpiresAt as number | undefined)
const getImpersonationCustomerLabel = createSelector(
  sharedSelectors.getCurrentTokenDecoded,
  token => (token as any)?.customerName ?? token?.customerKey ?? ''
)

// TenantAdmin client-switch (Story 1.5): the `switchedFrom` claim is stamped only on a token minted
// by `client-switch` (never on a normal or return session), so its presence marks a switched-client
// session that can Return to its tenant. Not on IE11Token — a runtime-only claim, cast confined here.
const getIsSwitchedClient = createSelector(sharedSelectors.getCurrentTokenDecoded, token => (token as any)?.switchedFrom != null)
const getIsSwitchedOrActingAs = createSelector(getIsImpersonating, getIsSwitchedClient, (impersonating, switched) => impersonating || switched)

export const selectors = {
  ...sharedSelectors,
  ...searchKeySelectors,
  ...customerSelectors,
  getCustomerKey,
  getCustomerKey$,
  getCustomerName$,
  getCustomerNameClaim,
  getIsImpersonating,
  getActAsExpiresAt,
  getImpersonationCustomerLabel,
  getIsSwitchedClient,
  getIsSwitchedOrActingAs,
}
